// MTD candlestick fetch machine for the Market Probabilities board, extracted from
// the provider so the ~1000-line provider keeps only the reducer/state, the
// coverage resolution, the context projection, and the composition of the two
// effect hooks. BEHAVIOUR-NEUTRAL lift: the effect body, the 60s poll, the 13s
// per-request watchdog, the per-request settled guard, and the teardown drain
// are preserved verbatim.
//
// Each covered market polls its month-to-date minutely SPOT series (the same
// candlestick/PERPETUAL wire LockedTokenHeader uses). From it:
//   start_of_month_spot = first open   -> the fixed ladder anchor
//   mtd_high = max(highs), mtd_low = min(lows)  -> banner + HIT logic
// A wrong-venue (or unanswered) request can drop with no callback; the 13s
// watchdog dispatches MTD_ERROR so the card shows the honest "no MTD data"
// state and the next poll retries.
import { useEffect, type MutableRefObject } from 'react';

import { asRecord } from '../exotics-engine/engine-wire';
import { CandleRequest } from '../model/candleData';
import { IntervalEnum } from '../../model/interval.enum';
import { floorToMinute, MTD_REFRESH_MS, MTD_TIMEOUT_MS } from './machinery';
import { MARKETS } from './markets';
import {
  startOfMonth,
  startOfYear,
  type ResolvedMarket,
} from '../../pages/market-probabilities/ladder';
import type { MarketProbabilitiesAction, MarketProbabilitiesState } from './state';

// Everything the MTD effect needs is owned by the provider and passed in, so
// the extraction preserves ref identity and behaviour exactly. The WS handles
// participate in the effect deps; the refs are stable (never in deps).
export interface UseMtdCandlesArgs {
  isConnected: boolean;
  sendMessage: ((message: string) => void) | undefined;
  subscribeToChannel: ((id: string, handler: (raw: unknown) => void) => void) | undefined;
  safeUnsubscribe: (reqId: string) => void;
  stateRef: MutableRefObject<MarketProbabilitiesState>;
  resolvedByTokenRef: MutableRefObject<Record<string, ResolvedMarket | null>>;
  dispatchRef: MutableRefObject<React.Dispatch<MarketProbabilitiesAction>>;
}

export function useMtdCandles({
  isConnected,
  sendMessage,
  subscribeToChannel,
  safeUnsubscribe,
  stateRef,
  resolvedByTokenRef,
  dispatchRef,
}: UseMtdCandlesArgs): void {
  useEffect(() => {
    if (!isConnected || !sendMessage || !subscribeToChannel) return;
    let cancelled = false;
    // In-flight MTD requests, keyed by reqId -> their watchdog timer. Each
    // request adds itself on send and removes itself on settle (handler OR
    // watchdog), so this stays bounded across the 60s polls. On teardown we
    // drain whatever is still open -- clear the watchdog timer and release the
    // channel subscription -- so a tab switch mid-flight can't orphan a 13s
    // timer (holding this closure) or a dangling handler subscription.
    const inFlight = new Map<string, ReturnType<typeof setTimeout>>();

    const fetchMtd = () => {
      const begin = floorToMinute(startOfMonth());
      const end = floorToMinute(new Date());
      if (end.getTime() <= begin.getTime()) return;

      for (const cfg of MARKETS) {
        const m = stateRef.current.markets[cfg.token];
        if (!m || !m.covered) continue;
        const resolved = resolvedByTokenRef.current[cfg.token];
        if (!resolved) continue;

        const reqId = `MARKET_PROB_MTD_${cfg.token}_${Date.now()}`;

        // Per-request settled guard + watchdog. settled is flipped by whichever
        // of {handler, timeout} fires first, so a late response can't undo an
        // already-dispatched error and the watchdog can't fire after a response.
        let settled = false;
        const watchdog = setTimeout(() => {
          if (settled || cancelled) return;
          settled = true;
          inFlight.delete(reqId);
          safeUnsubscribe(reqId);
          dispatchRef.current({ type: 'MTD_ERROR', token: cfg.token });
        }, MTD_TIMEOUT_MS);

        const handler = (raw: unknown) => {
          if (cancelled || settled) return;
          settled = true;
          clearTimeout(watchdog);
          inFlight.delete(reqId);
          safeUnsubscribe(reqId);
          const resp = asRecord(raw);
          if (!resp || resp.error || !Array.isArray(resp.result) || resp.result.length === 0) {
            dispatchRef.current({ type: 'MTD_ERROR', token: cfg.token });
            return;
          }
          const rows = resp.result as unknown[][];
          // Each candle row is [ts, open, high, low, close, ...].
          const opens = rows.map((r) => Number(r[1])).filter(Number.isFinite);
          const highs = rows.map((r) => Number(r[2])).filter(Number.isFinite);
          const lows = rows.map((r) => Number(r[3])).filter(Number.isFinite);
          if (opens.length === 0 || highs.length === 0 || lows.length === 0) {
            dispatchRef.current({ type: 'MTD_ERROR', token: cfg.token });
            return;
          }
          const somSpot = opens[0];
          if (!Number.isFinite(somSpot) || somSpot <= 0) {
            dispatchRef.current({ type: 'MTD_ERROR', token: cfg.token });
            return;
          }
          dispatchRef.current({
            type: 'SET_MTD',
            token: cfg.token,
            somSpot,
            mtdHigh: Math.max(...highs),
            mtdLow: Math.min(...lows),
          });
        };

        inFlight.set(reqId, watchdog);
        subscribeToChannel(reqId, handler);
        sendMessage(
          JSON.stringify(
            new CandleRequest(
              reqId,
              begin.toISOString(),
              end.toISOString(),
              IntervalEnum.ONE_MINUTE,
              resolved.source,
              // CANDLE symbol, NOT baseQuote: Deribit candles key off base_quote
              // (BTC) but Binance/MEXC want the venue symbol (XRPUSDT). Mirrors
              // the working perpetual fetch (market-data-provider.tsx -> the
              // requestToken = resolveCandlestickSymbolByExchange -> passed whole
              // as the CandleRequest underlying in use-perpetual-data.ts:55-63,
              // with 'PERPETUAL' as a SEPARATE label arg -- no -PERPETUAL split).
              // baseQuote stays the pricing-fan-out key (that path is correct).
              resolved.candleSymbol,
              'PERPETUAL',
            ),
          ),
        );
      }
    };

    const fetchYtd = () => {
      const begin = startOfYear();
      const end = floorToMinute(new Date());
      if (end.getTime() <= begin.getTime()) return;

      for (const cfg of MARKETS) {
        const m = stateRef.current.markets[cfg.token];
        if (!m || !m.covered) continue;
        const resolved = resolvedByTokenRef.current[cfg.token];
        if (!resolved) continue;

        const reqId = `MARKET_PROB_YTD_${cfg.token}_${Date.now()}`;

        let settled = false;
        const watchdog = setTimeout(() => {
          if (settled || cancelled) return;
          settled = true;
          inFlight.delete(reqId);
          safeUnsubscribe(reqId);
          dispatchRef.current({ type: 'YTD_ERROR', token: cfg.token });
        }, MTD_TIMEOUT_MS);

        const handler = (raw: unknown) => {
          if (cancelled || settled) return;
          settled = true;
          clearTimeout(watchdog);
          inFlight.delete(reqId);
          safeUnsubscribe(reqId);
          const resp = asRecord(raw);
          if (!resp || resp.error || !Array.isArray(resp.result) || resp.result.length === 0) {
            dispatchRef.current({ type: 'YTD_ERROR', token: cfg.token });
            return;
          }
          const rows = resp.result as unknown[][];
          const opens = rows.map((r) => Number(r[1])).filter(Number.isFinite);
          const highs = rows.map((r) => Number(r[2])).filter(Number.isFinite);
          const lows = rows.map((r) => Number(r[3])).filter(Number.isFinite);
          if (opens.length === 0 || highs.length === 0 || lows.length === 0) {
            dispatchRef.current({ type: 'YTD_ERROR', token: cfg.token });
            return;
          }
          const soySpot = opens[0];
          if (!Number.isFinite(soySpot) || soySpot <= 0) {
            dispatchRef.current({ type: 'YTD_ERROR', token: cfg.token });
            return;
          }
          dispatchRef.current({
            type: 'SET_YTD',
            token: cfg.token,
            soySpot,
            ytdHigh: Math.max(...highs),
            ytdLow: Math.min(...lows),
          });
        };

        inFlight.set(reqId, watchdog);
        subscribeToChannel(reqId, handler);
        sendMessage(
          JSON.stringify(
            new CandleRequest(
              reqId,
              begin.toISOString(),
              end.toISOString(),
              IntervalEnum.DAILY,
              resolved.source,
              resolved.candleSymbol,
              'PERPETUAL',
            ),
          ),
        );
      }
    };

    fetchMtd();
    const handle = setInterval(fetchMtd, MTD_REFRESH_MS);
    fetchYtd();
    const ytdHandle = setInterval(fetchYtd, MTD_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
      clearInterval(ytdHandle);
      for (const [reqId, watchdog] of inFlight) {
        clearTimeout(watchdog);
        safeUnsubscribe(reqId);
      }
      inFlight.clear();
    };
  }, [
    isConnected,
    sendMessage,
    subscribeToChannel,
    safeUnsubscribe,
    stateRef,
    resolvedByTokenRef,
    dispatchRef,
  ]);
}
